import { useCallback, useState } from "react";
import {
  Datagrid,
  TextField,
  Filter,
  TextInput,
  CreateButton,
  FunctionField,
  SelectInput,
  Record,
  Responsive,
  SimpleList,
  Identifier,
} from "react-admin";
import "../../common/styles/list-style.css";
import IndeterminateCheckBoxIcon from "@material-ui/icons/IndeterminateCheckBox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import { Box, useTheme, Theme, Tooltip } from "@material-ui/core";
import { useSelector } from "react-redux";
import { AppState } from "../../../common/types";
import { useListContext, useTranslate } from "react-admin";
import { IVehicleGridView } from "../../../common/data/data-types/vehicles";
import { ActionsField } from "../../common/data-grid-fields/ActionsField";
import { ArrayField } from "../../common/data-grid-fields/ArrayField";
import { IResourceListProps } from "../../common/ra-types";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import TruckOutlineIcon from "../../../common/icons/TruckOutlineIcon";
import permissionNames from "../../../common/auth/access-control/permissionNames";
import { useActionPermissions } from "../../../common/auth/useActionPermissions";
import { ProtectedElement } from "../../common/field-permissions/ProtectedElement";
import { useFieldPermissions } from "../../../common/hooks/useFieldPermissions";
import { protectedEntities } from "../../../common/auth/permissions";
import { FieldPermissionsContext } from "../../common/field-permissions/FieldPermissionsContext";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import { useAppData } from "../../../common/hooks/useAppData";
import GroupIcon from "@material-ui/icons/Group";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import Icon from "@mdi/react";
import {
  mdiCardAccountDetails,
  mdiShieldCar,
  mdiFileDocumentOutline,
} from "@mdi/js";
import { AppbarGroupsMultipleFilter } from "../../common/data-grid-inputs/AppBarGroupsMultipleFilter";

const ListFilter = (props: any) => {
  useSearchFields(props, ["make", "model", "name", "licensePlate", "vin"]);
  useResetListSearchField(props);
  const { loading } = useListContext();
  const companies = useAppData((x) => x.companies);
  return (
    <Filter {...props}>
      {!loading && (
        <TextInput
          className="vehicles-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
          autoFocus
          autoComplete="off"
        />
      )}
      {loading && (
        <TextInput
          className="vehicles-list-filter search-box"
          label="resources.common.labels.search"
          source="q"
          alwaysOn
        />
      )}
      <SelectInput
        className="filter-box"
        label="Filter by vehicle status"
        allowEmpty
        source="status"
        alwaysOn
        choices={[
          { id: "Active", name: "Active Vehicles" },
          { id: "Inactive", name: "Inactive Vehicles" },
        ]}
      />
      <SelectInput
        className="filter-box"
        label="Filter by company"
        allowEmpty
        source="companyId"
        alwaysOn
        choices={companies}
      />
      <SelectInput
        className="filter-box"
        label="Filter by ELD status"
        allowEmpty
        source="isEldConnected"
        alwaysOn
        choices={[
          { id: "true", name: "Connected" },
          { id: "false", name: "Not connected" },
        ]}
      />
    </Filter>
  );
};

export const VehiclesList = (props: IResourceListProps) => {
  const themeName = useSelector((state: AppState) => state.theme);
  const theme: Theme = useTheme();
  const translate = useTranslate();
  const permissions = useActionPermissions();
  const fieldPermissions = useFieldPermissions(
    protectedEntities.Vehicle,
    (x) => x.list
  );
  const [selectedGroups, setSelectedGroups] = useState<Array<Identifier>>([]);
  const handleSelectedGroupsChange = useCallback((e: Identifier[]) => {
    setSelectedGroups(e);
  }, []);

  const ListActions = () => {
    const { basePath } = useListContext();
    const permissions = useActionPermissions();
    return (
      <>
        {permissions &&
          permissions[permissionNames.admin.vehicles.main] &&
          permissions[permissionNames.admin.vehicles.create] && (
            <CreateButton basePath={basePath} />
          )}
      </>
    );
  };

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {record ? record.make + " | " + record.model : null}

        <FunctionField<IVehicleGridView>
          source="status"
          headerClassName="centered-content"
          label="resources.common.labels.active"
          render={(record) => (
            <Box className="centered-content">
              {record?.status === "Active" ? (
                <CheckBoxIcon
                  className={record.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.success.dark
                        : theme.palette.success.light,
                  }}
                />
              ) : (
                <IndeterminateCheckBoxIcon
                  className={record?.status}
                  style={{
                    color:
                      themeName === "dark"
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                  }}
                />
              )}
            </Box>
          )}
        />
      </div>
    );
  };

  const RenderSecondaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          marginTop: "14px",
        }}
      >
        <Tooltip title="Vehicle number">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <LocalShippingOutlinedIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="name">
              <TextField
                source="name"
                label="resources.admin-vehicles.labels.vehicleId"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="VIN">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <Icon
              style={{
                color: theme.palette.success.main,
              }}
              path={mdiCardAccountDetails}
              size={1}
            />

            <ProtectedElement source="vin">
              <TextField
                source="vin"
                label="resources.admin-vehicles.labels.vin"
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="ELD serial number">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <Icon
              style={{
                color: theme.palette.success.main,
              }}
              path={mdiShieldCar}
              size={1}
            />
            <ProtectedElement source="eldSerialNumber">
              <TextField
                source="eldSerialNumber"
                label="resources.admin-vehicles.labels.eldSerialNumber"
                emptyText={translate(
                  "resources.admin-vehicles.labels.noEldDevice"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="ELD serial number">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <GroupIcon
              style={{
                color: theme.palette.success.main,
              }}
            />
            <ProtectedElement source="groups">
              <ArrayField
                record={record}
                source="groups"
                label="resources.common.labels.groups"
                sortable={false}
                displayedElementsCount={1}
                emptyArrayDisplay={translate(
                  "resources.admin-vehicles.labels.noGroups"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
        <Tooltip title="Document names">
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            <Icon
              style={{
                color: theme.palette.success.main,
              }}
              path={mdiFileDocumentOutline}
              size={1}
            />
            <ProtectedElement source="documentNames">
              <ArrayField
                record={record}
                source="documentNames"
                label="resources.common.labels.documents"
                sortable={false}
                displayedElementsCount={1}
                emptyArrayDisplay={translate(
                  "resources.admin-vehicles.labels.noDocuments"
                )}
              />
            </ProtectedElement>
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <>
      <AppbarGroupsMultipleFilter
        selectedGroups={selectedGroups}
        onSelectedGroupsChange={handleSelectedGroupsChange}
      />
      <FieldPermissionsContext.Provider value={fieldPermissions}>
        <div className="content-container">
          <DefaultProLogsList
            empty={<ProLogsEmpty iconType={TruckOutlineIcon} />}
            className="content-list"
            title="resources.admin-vehicles.name"
            bulkActionButtons={false}
            actions={<ListActions />}
            filters={<ListFilter />}
            filter={
              selectedGroups.length
                ? { groupIds: selectedGroups.join(",") }
                : {}
            }
            sort={{ field: "name", order: "ASC" }}
            {...props}
          >
            <Responsive
              small={
                <SimpleList
                  className="simple-list-wrapper"
                  linkType="edit"
                  primaryText={(record) => (
                    <RenderPrimaryTextForSimpleList record={record} />
                  )}
                  secondaryText={(record) => (
                    <RenderSecondaryTextForSimpleList record={record} />
                  )}
                  rightIcon={(record) => {
                    return (
                      <ActionsField
                        {...props}
                        record={record}
                        hasShow={props.hasShow}
                        editable={() =>
                          permissions &&
                          permissions[permissionNames.admin.vehicles.main] &&
                          permissions[permissionNames.admin.vehicles.update]
                        }
                        canActivateOrDeactivate={() =>
                          permissions &&
                          permissions[permissionNames.admin.vehicles.main] &&
                          permissions[permissionNames.admin.vehicles.update]
                        }
                      />
                    );
                  }}
                />
              }
              medium={
                <Datagrid>
                  <ProtectedElement source="make">
                    <TextField
                      source="make"
                      label="resources.admin-vehicles.labels.make"
                    />
                  </ProtectedElement>
                  <ProtectedElement source="model">
                    <TextField
                      source="model"
                      label="resources.admin-vehicles.labels.model"
                    />
                  </ProtectedElement>
                  <ProtectedElement source="name">
                    <TextField
                      source="name"
                      label="resources.admin-vehicles.labels.vehicleId"
                    />
                  </ProtectedElement>
                  <ProtectedElement source="year">
                    <TextField source="year" label="Year" />
                  </ProtectedElement>
                  <ProtectedElement source="vin">
                    <TextField
                      source="vin"
                      label="resources.admin-vehicles.labels.vin"
                    />
                  </ProtectedElement>
                  <TextField source="fuelType" label="Fuel type" />
                  <ProtectedElement source="eldSerialNumber">
                    <TextField
                      source="eldSerialNumber"
                      label="resources.admin-vehicles.labels.eldSerialNumber"
                      emptyText={translate(
                        "resources.admin-vehicles.labels.noEldDevice"
                      )}
                    />
                  </ProtectedElement>
                  <ProtectedElement source="groups">
                    <ArrayField
                      source="groups"
                      label="resources.common.labels.groups"
                      sortable={false}
                      displayedElementsCount={1}
                      emptyArrayDisplay={translate(
                        "resources.admin-vehicles.labels.noGroups"
                      )}
                    />
                  </ProtectedElement>
                  <FunctionField<IVehicleGridView>
                    source="status"
                    headerClassName="centered-content"
                    label="resources.common.labels.active"
                    render={(record) => (
                      <Box className="centered-content">
                        {record?.status === "Active" ? (
                          <CheckBoxIcon
                            className={record.status}
                            style={{
                              color:
                                themeName === "dark"
                                  ? theme.palette.success.dark
                                  : theme.palette.success.light,
                            }}
                          />
                        ) : (
                          <IndeterminateCheckBoxIcon
                            className={record?.status}
                            style={{
                              color:
                                themeName === "dark"
                                  ? theme.palette.error.dark
                                  : theme.palette.error.light,
                            }}
                          />
                        )}
                      </Box>
                    )}
                  />
                  <ProtectedElement source="documentNames">
                    <ArrayField
                      source="documentNames"
                      label="resources.common.labels.documents"
                      sortable={false}
                      displayedElementsCount={1}
                      emptyArrayDisplay={translate(
                        "resources.admin-vehicles.labels.noDocuments"
                      )}
                    />
                  </ProtectedElement>
                  <ActionsField
                    hasShow={props.hasShow}
                    editable={() =>
                      permissions &&
                      permissions[permissionNames.admin.vehicles.main] &&
                      permissions[permissionNames.admin.vehicles.update]
                    }
                    canActivateOrDeactivate={() =>
                      permissions &&
                      permissions[permissionNames.admin.vehicles.main] &&
                      permissions[permissionNames.admin.vehicles.update]
                    }
                  />
                </Datagrid>
              }
            />
          </DefaultProLogsList>
        </div>
      </FieldPermissionsContext.Provider>
    </>
  );
};
